import Modal from 'Common/Components/Modal';
import { AnimatedListSkeleton, AnimatedTable, AnimatedTableBody, AnimatedTableRow } from 'components/animations';
import ButtonBase from 'components/ui/buttons/ButtonBase';
import React, { FC, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { RootState } from '@redux/store';
import { getProductLifeCycleThunk } from '@redux/features/product.slice';
import { dateFormatter } from 'helpers/formatter_helper';

type IProps = {
    show: boolean;
    handleClose: () => void;
    productId: number;
};

const ProductLifeCycleModal: FC<IProps> = ({ show, handleClose, productId }) => {
    const dispatch = useAppDispatch();
    const { productLifeCycle, productLifeCycleLoading } = useAppSelector(
        (state: RootState) => state.productState
    );

    useEffect(() => {
        if (show && productId > 0) {
            dispatch(getProductLifeCycleThunk(productId));
        }
    }, [show, productId, dispatch]);

    return (
        <Modal
            show={show}
            onHide={handleClose}
            dialogClassName="w-full bg-white p-3 shadow rounded-md overflow-scroll md:w-[90%] md:max-w-[1200px] md:max-h-[80vh]"
        >
            <Modal.Header>
                <Modal.Title className="text-lg font-semibold">Product Life Cycle - Product ID: {productId}</Modal.Title>
            </Modal.Header>
            <Modal.Body className="my-5">
                {productLifeCycleLoading ? (
                    <div className="mx-auto flex my-6 items-center justify-center rounded-md bg-white">
                        <div className="size-10 animate-spin rounded-full border-4 border-custom-500 border-l-transparent" />
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <AnimatedTable className="table-custom w-full border-separate border-spacing-y-1 whitespace-nowrap">
                            <thead className="ltr:text-left rtl:text-right">
                                <tr
                                    className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                    after:left-0 after:top-0 after:border-l-2
                    after:border-transparent [&.active]:after:border-custom-500"
                                >
                                    <th className="px-3.5 py-2.5 font-semibold uppercase">ID</th>
                                    <th className="px-3.5 py-2.5 font-semibold uppercase">Event Type</th>
                                    <th className="px-3.5 py-2.5 font-semibold uppercase">Quantity</th>
                                    <th className="px-3.5 py-2.5 font-semibold uppercase">Serial Number</th>
                                    <th className="px-3.5 py-2.5 font-semibold uppercase">Product ID</th>
                                    <th className="px-3.5 py-2.5 font-semibold uppercase">Product Batch ID</th>
                                    <th className="px-3.5 py-2.5 font-semibold uppercase">Event Timestamp</th>
                                    <th className="px-3.5 py-2.5 font-semibold uppercase">Performed By</th>
                                </tr>
                            </thead>
                            <AnimatedTableBody loading={false} itemCount={productLifeCycle?.length ?? 0} animationKey={`plc-modal`}>
                                {productLifeCycle && productLifeCycle.length > 0 ? (
                                    productLifeCycle.map((event: any) => (
                                        <AnimatedTableRow
                                            rowKey={event?.id}
                                            detailRecord={event}
                                            className="relative rounded-md bg-slate-50 after:absolute after:bottom-0
                        after:left-0 after:top-0 after:border-l-2 hover:bg-[#bd902e10]
                        after:border-transparent [&.active]:after:border-custom-500"
                                        >
                                            <td className="px-3.5 py-2.5">{event?.id || `-`}</td>
                                            <td className="px-3.5 py-2.5">{event?.eventType || `-`}</td>
                                            <td className="px-3.5 py-2.5">{event?.quantity ?? `-`}</td>
                                            <td className="px-3.5 py-2.5">{event?.serialNumber || `-`}</td>
                                            <td className="px-3.5 py-2.5">{event?.productId || `-`}</td>
                                            <td className="px-3.5 py-2.5">{event?.productBatchId ?? `-`}</td>
                                            <td className="px-3.5 py-2.5">
                                                {event?.eventTimestamp
                                                    ? dateFormatter(event.eventTimestamp)
                                                    : `-`}
                                            </td>
                                            <td className="px-3.5 py-2.5">{event?.performedBy || `-`}</td>
                                        </AnimatedTableRow>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={8} className="px-3.5 py-2.5 text-center text-gray-500">
                                            No product lifecycle events found
                                        </td>
                                    </tr>
                                )}
                            </AnimatedTableBody>
                        </AnimatedTable>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="flex items-center justify-center gap-2 mt-5">
                <ButtonBase
                    innerText="Close"
                    className="bg-gray-500 hover:bg-gray-600 rounded border-none px-4 py-2 text-white"
                    onClick={handleClose}
                />
            </Modal.Footer>
        </Modal>
    );
};

export default ProductLifeCycleModal;

